<!-- eslint-disable vuejs-accessibility/label-has-for -->
<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<script setup lang="ts">
import type { CannedTextGroup } from '@src/components/report-generator/report-reusables/report-typing';

import { useReportStore } from '@src/store/report';
import { useUpdatedReportStore } from '@src/store/report-store';
import { computed, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    editable?: boolean;
    cannedTextGroups?: CannedTextGroup[] | null;
    fieldKey?: string | null;
  }>(),
  {
    editable: false,
    cannedTextGroups: null,
    fieldKey: null,
  },
);

const selectContainer = ref(false);
const optionsContainer = ref<HTMLDivElement | null>(null);

const reportStore = useReportStore();
const updatedReportStore = useUpdatedReportStore();

const hoverTimer = ref<ReturnType<typeof setTimeout> | null>(null);

const defaultGroups: CannedTextGroup[] = [
  {
    id: 'bmoreChoices',
    title: 'BMORE Quality:',
    isMultiSelect: true,
    choices: [
      {
        label: 'Normal',
        text: 'There is a normal myocardial oxygenation response to a vasoactive breathing manoeuvre in all coronary territories.',
      },
      {
        label: 'Globally Blunted',
        text: 'There is a blunted myocardial oxygenation response to a vasoactive breathing manoeuvre involving all coronary territories.',
      },
      {
        label: 'Regionally Blunted',
        text: 'There is a blunted oxygenation response to a vasoactive breathing manoeuvre in the - territory, with a normal response in all remaining myocardial territories.',
      },
      {
        label: 'Regionally Impaired',
        text: 'Due to an artefact, the image quality is impaired in the - territory. A normal myocardial oxygenation response to a vasoactive breathing manoeuvre is observed in the remaining coronary territories.',
      },
      {
        label: 'Non-diagnostic',
        text: 'Due to artefacts, the image quality is insufficient for quantifying the myocardial oxygenation response.',
      },
    ],
  },
  {
    id: 'HRChoices',
    title: 'Heart Rate Response:',
    isMultiSelect: false,
    choices: [
      {
        label: 'High (>25)',
        text: 'The heart rate response indicates good neuro-humoral responsiveness.',
      },
      {
        label: 'Normal (15 - 25)',
        text: 'The heart rate response indicates normal neuro-humoral responsiveness.',
      },
      {
        label: 'Low (<15)',
        text: 'The heart rate response indicates low neuro-humoral responsiveness.',
      },
    ],
  },
  {
    id: 'optionalChoices',
    title: 'Optional:',
    isMultiSelect: true,
    choices: [
      {
        label: 'Reduced Diagnostic Confidence',
        text: 'Reduced diagnostic confidence due to motion artefacts early in the breath-hold, limiting reliable interpretation. Taking these limitations into account, there appears to be a normal myocardial oxygenation response to a vasoactive breathing manoeuvre in all coronary territories.',
      },
    ],
  },
];

const computedGroups = computed<CannedTextGroup[]>(() => {
  return props.cannedTextGroups && props.cannedTextGroups.length > 0
    ? props.cannedTextGroups
    : defaultGroups;
});

// selections is Record<groupId, Record<choiceLabel, boolean>>
const selections = ref<Record<string, Record<string, boolean>>>({});

watch(
  computedGroups,
  (groups) => {
    const nextSelections: Record<string, Record<string, boolean>> = {};
    groups.forEach((group) => {
      nextSelections[group.id] = {};
      group.choices.forEach((choice) => {
        nextSelections[group.id][choice.label] = false;
      });
    });
    selections.value = nextSelections;
  },
  { immediate: true, deep: true },
);

function handleChoice(choiceLabel: string, groupId: string) {
  const group = computedGroups.value.find(g => g.id === groupId);
  if (!group) return;

  const currentVal = !!selections.value[groupId]?.[choiceLabel];
  const newVal = !currentVal;

  if (!group.isMultiSelect) {
    // Clear other options
    const groupSelections: Record<string, boolean> = {};
    group.choices.forEach((c) => {
      groupSelections[c.label] = false;
    });
    groupSelections[choiceLabel] = newVal;
    selections.value = {
      ...selections.value,
      [groupId]: groupSelections,
    };
  }
  else {
    selections.value = {
      ...selections.value,
      [groupId]: {
        ...selections.value[groupId],
        [choiceLabel]: newVal,
      },
    };
  }
}

function clearHoverTimer() {
  if (hoverTimer.value) {
    clearTimeout(hoverTimer.value);
    hoverTimer.value = null;
  }
}

function toggleContainer() {
  clearHoverTimer();
  selectContainer.value = !selectContainer.value;

  if (optionsContainer.value) {
    if (selectContainer.value) {
      optionsContainer.value.style.pointerEvents = 'all';
    }
    else {
      optionsContainer.value.style.pointerEvents = 'none';
    }
  }
}

function handleOptionSelect() {
  let text = '';
  computedGroups.value.forEach((group) => {
    let groupText = '';
    group.choices.forEach((choice) => {
      if (selections.value[group.id]?.[choice.label]) {
        groupText += `${choice.text}\n`;
      }
    });
    if (groupText) {
      text += groupText;
      if (group.id === 'bmoreChoices') {
        text += '\n';
      }
    }
  });

  if (props.fieldKey) {
    updatedReportStore.setValue(props.fieldKey, text);
  }
  else {
    reportStore.report.summary = text;
  }
  toggleContainer();
}

function startHoverTimer() {
  hoverTimer.value = setTimeout(() => {
    if (!selectContainer.value) {
      toggleContainer();
    }
  }, 50);
}
</script>

<template>
  <div v-if="editable" ref="optionsContainer" class="options-container">
    <Transition name="bounce">
      <div v-if="selectContainer" class="container-list">
        <div class="coronary-choices">
          <template v-for="(group, gIdx) in computedGroups" :key="group.id">
            <div v-if="gIdx > 0" class="vertical-divider" />
            <div
              class="row-container"
              :style="gIdx === 0 ? 'min-width: 40%' : undefined"
            >
              <h4>{{ group.title }}</h4>
              <div class="choices-container">
                <div
                  v-for="choice in group.choices"
                  :key="choice.label"
                  class="choice"
                  @click="handleChoice(choice.label, group.id)"
                >
                  <i
                    v-if="selections[group.id]?.[choice.label]"
                    class="fa-solid fa-circle-check fa-xs"
                    style="color: #ffffff"
                  />

                  <i
                    v-else
                    class="fa-regular fa-circle fa-xs"
                    style="color: #ffffff"
                  />
                  <label> {{ choice.label }}</label>
                </div>
              </div>
            </div>
          </template>

          <div class="add-container">
            <div class="choice" @click="handleOptionSelect">
              <i class="fa-solid fa-plus fa-xs" style="color: white" />
              <label>Add to Report</label>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <div
      class="select-options-container"
      @click="toggleContainer"
      @mouseenter="startHoverTimer"
      @mouseleave="clearHoverTimer"
    >
      <i
        class="fa-regular fa-rectangle-list"
        :style="{ color: selectContainer ? 'white' : undefined }"
      />
    </div>
  </div>
</template>

<style scoped>
.options-container {
  pointer-events: none;
  /* pointer-events: all; */
  position: absolute;
  width: 100%;
  max-height: 100%;
  height: 100%;
  border-radius: 5px;
}
.coronary-choices {
  padding-left: 10px;
  display: flex;
  height: 100%;
  width: 100%;
  gap: 5px;
}
.row-container {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-width: 150px;
}

.vertical-divider {
  background: rgb(186, 186, 186);
  width: 2px;
  height: 80%;
  align-self: center;
  border-radius: 7px;
}
.container-list {
  background: #0f2027; /* fallback for old browsers */
  background: -webkit-linear-gradient(to right, #182d36, #142429, #142429e1, #0f2027b7);
  background: linear-gradient(to right, #182d36, #142429, #142429e1, #0f2027b7);

  border-radius: 5px;
  width: 100%;
  height: 100%;
  max-height: 100%;
  overflow: scroll;
  overflow-x: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.container-list::-webkit-scrollbar {
  display: none;
  width: 0 !important;
}

.container-list h4 {
  color: white;
  font-size: 12px;
  margin-top: 5px;
  margin-bottom: 10px;
}

.choices-container {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  align-items: center;
  /* justify-content: space-evenly; */
}

.coronary-choices label {
  color: white;
  font-size: 11px;
  pointer-events: none;
  font-weight: bold;
}

.choice {
  border: 1px solid rgb(156, 155, 155);
  max-width: 150px;
  padding: 2px 8px 2px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border-radius: 5px;
  cursor: pointer;
}

.add-container {
  position: absolute;
  bottom: 5px;
  right: 5px;
}

.choice:hover {
  background: rgba(156, 155, 155, 0.356);
  transition: all 0.3s ease;
}

.coronary-choices input {
  font-size: 13px;
  cursor: pointer;
}

.option {
  color: white;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 5px;
  padding-bottom: 5px;
  cursor: pointer;
  background: #2020209f;
}

.option:hover {
  background: #229ed78f;
}

.select-options-container {
  position: absolute;
  right: 5px;
  top: 5px;
  pointer-events: all;
  cursor: pointer;
  padding: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
}

.artifact-text {
  height: 12px;
  width: 22px;
  padding-left: 5px;
  resize: none;
  overflow: hidden;
  outline: none;
  background: none;
  color: rgba(255, 255, 255, 0.483);
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 12px; /* Adjust to vertically center the text */
  /* border: 1px dashed rgba(255, 255, 255, 0.522); */
  border-radius: 5px;
  transition: all 0.3s ease;
}

.active {
  color: white;
  width: 85px;
}

.select-options-container:hover {
  background: #0f2027;
  color: white;
  transition: all 0.3s ease;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.bounce-enter-active {
  animation: bounce-in 0.3s;
}
.bounce-leave-active {
  opacity: 0;
  transition: all 0.3s ease;
}
@keyframes bounce-in {
  0% {
    opacity: 0.5;
    transform: translateX(200px);
  }

  100% {
    opacity: 1;
  }
}
</style>
